import React, { FunctionComponent, useEffect, useState } from 'react';

import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';

import Text from 'common/components/Text/Text';
import { SessionState, setProfile, setUser } from 'features/session/sessionSlice';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { CommonHash } from 'app/types';
import PersonalForm from './PersonalForm';
import AcademicBackground from './AcademicBackground';
import TestsForm from './TestsForm';
import { countMatchedElements, navigateTop, updateProfileAnswers, userAnswers } from 'common/utils';
import WorkExperience from './WorkExperience';
import { useUpdateUserMutation, useUpdateBackofficeUserMutation } from 'services/exploreApi';
import { User, ApiObject } from 'app/types';

import styles from './UserInformation.module.scss';
import {
  PersonalFormImg,
  AcademicFormImg,
  WorkFormImg,
  TestsFormImg,
} from 'assets/session/dashboard/index';
import { setLastStepDone, setProfileCompleted, setProfileProgress, UserState } from '../userSlice';
import {
  ACADEMIC_FORM_ANSWERS,
  PERSONAL_FORM_ANSWERS,
  TESTS_FORM_ANSWERS,
  WORK_FORM_ANSWERS,
} from 'common/constants';

const titles = ['Personal information', 'Academic background', 'Work Experience', 'Test score'];
const headerImages = [PersonalFormImg, AcademicFormImg, WorkFormImg, TestsFormImg];

const UserInformation: FunctionComponent = () => {
  let updatedUser: ApiObject<User> | undefined;
  let updateUser: any;
  let isLoading: boolean;
  const { state: postulationId } = useLocation();
  let navigateRoute: string;
  const location = useLocation();
  const { profile, user } = useAppSelector(({ session }: { session: SessionState }) => session);
  let breadCrumbs: string[];
  if (location.pathname.includes('backoffice')) {
    [updateUser, { data: updatedUser, isLoading }] = useUpdateBackofficeUserMutation();
    navigateRoute = postulationId
      ? `/backoffice/users/${user?.id}/applications/${postulationId}`
      : `/backoffice/users/${user?.id}`;
    breadCrumbs = [
      `/backoffice/users/${user?.id}`,
      `/backoffice/users/${user?.id}/personal_information`,
    ];
  } else {
    [updateUser, { data: updatedUser, isLoading }] = useUpdateUserMutation();
    navigateRoute = postulationId ? `/home/applications/${postulationId}` : `/home`;
    breadCrumbs = [`/home`, `/home/personal_information`];
  }
  const { profileProgress, profileCompleted } = useAppSelector(
    ({ user }: { user: UserState }) => user
  );
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [answers, setAnswer] = useState<CommonHash>({});
  const [saveForm, setSaveForm] = useState<boolean>(false);
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const activeStep = (step: number) => {
    return currentStep == step;
  };
  const onPrevStep = () => {
    if (currentStep) {
      setCurrentStep(currentStep - 1);
    } else {
      navigate(-1);
    }
  };

  useEffect(() => {
    if (user) {
      setAnswer(userAnswers(user, answers));
      const profileProgress = {
        personal: countMatchedElements(PERSONAL_FORM_ANSWERS, user) / PERSONAL_FORM_ANSWERS.length,
        academic: user.academic_history?.[0]
          ? countMatchedElements(ACADEMIC_FORM_ANSWERS, user, 'academic_history') /
            ACADEMIC_FORM_ANSWERS.length
          : 0,
        work: user.work_history?.[0]
          ? user.have_working_experience
            ? countMatchedElements(WORK_FORM_ANSWERS, user, 'work_history') /
              WORK_FORM_ANSWERS.length
            : 1
          : 0,
        tests:
          countMatchedElements(TESTS_FORM_ANSWERS, user, 'english_exams') /
          TESTS_FORM_ANSWERS.length,
      };
      dispatch(setProfileProgress(profileProgress));
    }
  }, [user]);

  useEffect(() => {
    let total = 0;
    Object.keys(profileProgress).forEach((key) => {
      total += profileProgress[key];
    });
    const result = Math.round((total / 4) * 100);
    setTimeout(() => {
      if (result === 100) {
        !profileCompleted ? dispatch(setLastStepDone(1)) : '';
        dispatch(setProfileCompleted(true));
      } else {
        dispatch(setProfileCompleted(false));
      }
    }, 100);
  }, [profileProgress]);

  useEffect(() => {
    if (saveForm && user) {
      const formData = new FormData();
      Object.keys(answers).forEach((key) => {
        if (key === 'academic_history') {
          const histories: any = [];
          const posibleFiles = ['transcripts', 'degree_certificate'];
          const fileHash: any = {};
          answers['academic_history'].map((history: CommonHash, index: number) => {
            const h: CommonHash = {};
            Object.keys(history).map((historyKey) => {
              if (historyKey !== 'degree_certificate' && historyKey !== 'transcripts') {
                h[historyKey] = history[historyKey];
              }
              if (posibleFiles.includes(historyKey)) {
                if (!fileHash[index]) {
                  fileHash[index] = {};
                }
                fileHash[index][historyKey] = history[historyKey];
              }
            });
            histories.push(h);
          });
          formData.append(`user[academic_history]`, JSON.stringify(histories));
          Object.keys(fileHash).forEach((index) => {
            const fileInfo = fileHash[index];
            Object.keys(fileInfo).forEach((key) => {
              const file = fileInfo[key];
              formData.append(`user[academic_history_files][${index}][${key}]`, file);
            });
          });
        } else if (key === 'work_history') {
          const histories: any = [];
          answers['work_history'].map((history: CommonHash) => {
            const h: CommonHash = {};
            Object.keys(history).map((historyKey) => {
              h[historyKey] = history[historyKey];
            });
            histories.push(h);
          });
          formData.append(`user[work_history]`, JSON.stringify(histories));
        } else if (key === 'english_exams') {
          const english_exams: any = [];
          const fileHash: any = {};
          answers['english_exams'].map((exam: CommonHash, index: number) => {
            const h: CommonHash = {};
            Object.keys(exam).map((examKey) => {
              if (examKey !== 'english_exam') {
                h[examKey] = exam[examKey];
              }
              if (examKey === 'english_exam') {
                if (!fileHash[index]) {
                  fileHash[index] = {};
                }
                fileHash[index][examKey] = exam[examKey];
              }
            });
            english_exams.push(h);
          });
          formData.append(`user[english_exams]`, JSON.stringify(english_exams));
          Object.keys(fileHash).forEach((index) => {
            const fileInfo = fileHash[index];
            Object.keys(fileInfo).forEach((key) => {
              const file = fileInfo[key];
              formData.append(`user[english_exam_files][${index}][${key}]`, file);
            });
          });
        } else if (key === 'technical_exams') {
          const technical_exams: any = [];
          const fileHash: any = {};
          answers['technical_exams'].map((exam: CommonHash, index: number) => {
            const h: CommonHash = {};
            Object.keys(exam).map((examKey) => {
              if (examKey !== 'technical_exam') {
                h[examKey] = exam[examKey];
              }
              if (examKey === 'technical_exam') {
                if (!fileHash[index]) {
                  fileHash[index] = {};
                }
                fileHash[index][examKey] = exam[examKey];
              }
            });
            technical_exams.push(h);
          });
          formData.append(`user[technical_exams]`, JSON.stringify(technical_exams));
          Object.keys(fileHash).forEach((index) => {
            const fileInfo = fileHash[index];
            Object.keys(fileInfo).forEach((key) => {
              const file = fileInfo[key];
              formData.append(`user[technical_exam_files][${index}][${key}]`, file);
            });
          });
        } else {
          formData.append(`user[${key}]`, answers[key]);
        }
      });
      updateUser({ data: formData, id: user.id });
    }
  }, [saveForm]);

  useEffect(() => {
    if (updatedUser && user) {
      dispatch(setUser({ ...updatedUser.data }));
      dispatch(setProfile(updateProfileAnswers(updatedUser.data, profile)));
      navigate(navigateRoute);
      navigateTop();
    }
  }, [updatedUser]);

  useEffect(() => {
    if (!user?.['academic_history'] || Object.keys(user?.['academic_history']).length === 0) {
      setAnswer((a: any) => ({
        ...a,
        academic_history: [],
      }));
    }
    if (!user?.['work_history'] || Object.keys(user?.['work_history']).length === 0) {
      setAnswer((a: any) => ({
        ...a,
        work_history: [],
      }));
    }
    if (!user?.['work_history'] || Object.keys(user?.['work_history']).length === 0) {
      setAnswer((a: any) => ({
        ...a,
        work_history: [],
      }));
    }
    if (!user?.['english_exams'] || Object.keys(user?.['english_exams']).length === 0) {
      setAnswer((a: any) => ({
        ...a,
        english_exams: [],
      }));
    }
    if (!user?.['technical_exams'] || Object.keys(user?.['technical_exams']).length === 0) {
      setAnswer((a: any) => ({
        ...a,
        technical_exams: [],
      }));
    }
  }, []);

  return (
    <div className={`${isMobile ? '' : 'container d-flex flex-row'}`}>
      <div className={styles.sectionContainer}>
        {isMobile ? (
          <div className={styles.header}>
            <div className={styles.prevStepIcon} onClick={onPrevStep}>
              <i className="bi bi-chevron-left"></i>
            </div>
            <Text size={18} weight={700} color="primary">
              {titles[currentStep]}
            </Text>
            <img
              src={headerImages[currentStep]}
              className={styles.image}
              style={{ right: activeStep(3) ? '37px' : '0' }}
            ></img>
          </div>
        ) : (
          <div>
            <div className={styles.breadCrumbs}>
              <Link to={breadCrumbs[0]} relative="path">
                <Text size={16} weight={500}>
                  {'Dashboard >'}
                </Text>
              </Link>
              <Link to={breadCrumbs[1]} relative="path">
                <Text size={16} weight={500}>
                  {'Profile'}
                </Text>
              </Link>
            </div>
            <div className={styles.desktopHeader}>
              <div>
                <Text size={24} weight={700} color="primary" inlineStyle={{ marginTop: '30px' }}>
                  Profile
                </Text>
                <Text size={18} color="secondary" inlineStyle={{ marginTop: '10px' }}>
                  Please complete the information, this is crucial for applying to any university.
                </Text>
              </div>
              <div>
                <button
                  className={styles.saveButton}
                  onClick={() => setSaveForm(true)}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <div className={`spinner-border ${styles.loading}`} role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  ) : (
                    'Save & Leave'
                  )}
                </button>
              </div>
            </div>
            <div className={styles.tabsContainer}>
              <div className={styles.tabsOptions}>
                <div
                  className={`${styles.tab} ${activeStep(0) ? styles.activeTab : ''}`}
                  onClick={() => setCurrentStep(0)}
                >
                  Personal information
                </div>
                <div
                  className={`${styles.tab} ${activeStep(1) ? styles.activeTab : ''}`}
                  onClick={() => setCurrentStep(1)}
                >
                  Academic background
                </div>
                <div
                  className={`${styles.tab} ${activeStep(2) ? styles.activeTab : ''}`}
                  onClick={() => setCurrentStep(2)}
                >
                  Work Experience
                </div>
                <div
                  className={`${styles.tab} ${activeStep(3) ? styles.activeTab : ''}`}
                  onClick={() => setCurrentStep(3)}
                >
                  Test scores
                </div>
              </div>
            </div>
          </div>
        )}
        {activeStep(0) && (
          <PersonalForm
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
            answers={answers}
            setAnswer={setAnswer}
            setSaveForm={setSaveForm}
          />
        )}
        {activeStep(1) && (
          <AcademicBackground
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
            answers={answers}
            setAnswer={setAnswer}
            setSaveForm={setSaveForm}
          />
        )}
        {activeStep(2) && (
          <WorkExperience
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
            answers={answers}
            setAnswer={setAnswer}
            setSaveForm={setSaveForm}
          />
        )}
        {activeStep(3) && (
          <TestsForm answers={answers} setAnswer={setAnswer} setSaveForm={setSaveForm} />
        )}
      </div>
    </div>
  );
};

export default UserInformation;
